import React, { useState, useEffect } from 'react';
import { Brain, BookOpen, Settings, Plus, History, Send, Check, X, RefreshCw, Shield, Lightbulb, Sparkles, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LearningPanel = ({ token, user, isScanning = false, scanTopic = '' }) => {
  const [activeTab, setActiveTab] = useState('learn');
  const [evolutions, setEvolutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: 'doctrine',
    target: '',
    content: ''
  });

  // Vérifier si l'utilisateur est autorisé (Amiral ou CDT (R) Giraud)
  const isAuthorized = user?.email && (
    user.email.toLowerCase() === 'laurent.bechler@intradef.gouv.fr' ||
    user.email.toLowerCase() === 'jbgiraud@mac.com'
  );

  // Charger l'historique des évolutions
  const fetchEvolutions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/evolutions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEvolutions(data.evolutions || []);
      }
    } catch (error) {
      console.error('Erreur chargement évolutions:', error);
    }
  };

  useEffect(() => {
    if (isAuthorized) {
      fetchEvolutions();
      fetchGaps();
    }
  }, [token, isAuthorized]);

  // Charger les lacunes détectées
  const fetchGaps = async () => {
    setGapsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/auto-learning/gaps`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGaps(data.gaps || []);
      }
    } catch (error) {
      console.error('Erreur chargement lacunes:', error);
    } finally {
      setGapsLoading(false);
    }
  };

  // Afficher la suggestion quand un scan est en cours
  useEffect(() => {
    if (isScanning && isAuthorized && !aiSuggestion) {
      setShowSuggestion(true);
    }
  }, [isScanning, isAuthorized]);

  // Générer une suggestion d'apprentissage IA
  const generateSuggestion = async () => {
    setSuggestionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/learning/suggestion`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          topic: scanTopic || 'analyse géopolitique',
          context: 'scan_en_cours'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiSuggestion(data);
        toast.success('Suggestion générée !');
      } else {
        // Fallback suggestion statique si l'API échoue
        setAiSuggestion({
          type: 'doctrine',
          suggestion: `Pendant l'analyse de "${scanTopic || 'votre sujet'}", souhaitez-vous enrichir la base de connaissances avec des informations spécifiques sur cette région ?`,
          prompts: [
            'Ajouter une nouvelle doctrine pour cette zone',
            'Signaler une évolution récente',
            'Corriger une information obsolète'
          ]
        });
      }
    } catch (error) {
      console.error('Erreur génération suggestion:', error);
      setAiSuggestion({
        type: 'general',
        suggestion: 'Pendant que le scan est en cours, vous pouvez enrichir la base de connaissances du système.',
        prompts: ['Ajouter une information doctrine', 'Signaler une nouvelle menace']
      });
    } finally {
      setSuggestionLoading(false);
    }
  };

  // Soumettre un apprentissage
  const handleLearn = async () => {
    if (!formData.content) {
      toast.error('Veuillez entrer un contenu');
      return;
    }

    setLoading(true);
    
    let params = '';
    if (formData.type === 'doctrine') {
      params = `doctrine: ${formData.content}`;
    } else if (formData.type === 'adversaire') {
      if (!formData.target) {
        toast.error('Veuillez sélectionner un adversaire');
        setLoading(false);
        return;
      }
      params = `adversaire ${formData.target}: ${formData.content}`;
    } else if (formData.type === 'zone') {
      if (!formData.target) {
        toast.error('Veuillez sélectionner une zone');
        setLoading(false);
        return;
      }
      params = `zone ${formData.target}: ${formData.content}`;
    } else if (formData.type === 'menace') {
      params = `menace: ${formData.content}`;
    }

    try {
      const response = await fetch(`${API_URL}/api/email/test-command-body`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ command: 'learn', params })
      });

      const data = await response.json();
      
      if (data.result?.status === 'success') {
        toast.success('Apprentissage enregistré ! Email de confirmation envoyé.');
        setFormData({ ...formData, content: '', target: '' });
        fetchEvolutions();
      } else {
        toast.error(data.result?.message || 'Erreur lors de l\'apprentissage');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Soumettre une configuration
  const handleConfig = async (configType, configParams) => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/email/test-command-body`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ command: 'config', params: configParams })
      });

      const data = await response.json();
      
      if (data.result?.status === 'success') {
        toast.success('Configuration mise à jour ! Email de confirmation envoyé.');
        fetchEvolutions();
      } else {
        toast.error(data.result?.message || 'Erreur de configuration');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="bg-black border-2 border-red-500/50 p-6" data-testid="learning-panel-unauthorized">
        <div className="flex items-center gap-3 text-red-400">
          <Shield className="w-6 h-6" />
          <div>
            <p className="font-mono text-sm font-bold">ACCÈS RESTREINT</p>
            <p className="font-mono text-xs text-red-400/70">
              La reconfiguration du système est réservée à l'Amiral et au CDT (R) Giraud.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black border-2 border-purple-500/50" data-testid="learning-panel">
      {/* Suggestion Interactive pendant le scan */}
      {showSuggestion && isScanning && (
        <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 border-b border-purple-500/30 p-4">
          <div className="flex items-start gap-3">
            <div className="bg-purple-500/20 p-2 rounded-full">
              <Lightbulb className="w-5 h-5 text-yellow-400 animate-pulse" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span className="font-mono text-purple-300 text-sm font-bold">
                  Pendant que je réalise le scan...
                </span>
              </div>
              
              {!aiSuggestion ? (
                <div className="space-y-3">
                  <p className="font-mono text-white/80 text-sm">
                    Voulez-vous m'apprendre quelque chose sur <span className="text-purple-400">{scanTopic || 'ce sujet'}</span> ?
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={generateSuggestion}
                      disabled={suggestionLoading}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 font-mono text-xs uppercase flex items-center gap-2 rounded"
                    >
                      {suggestionLoading ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <MessageSquare className="w-3 h-3" />
                      )}
                      Oui, suggère-moi
                    </button>
                    <button
                      onClick={() => setShowSuggestion(false)}
                      className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 font-mono text-xs uppercase rounded"
                    >
                      Plus tard
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="font-mono text-white/90 text-sm bg-black/30 p-3 rounded border-l-2 border-purple-500">
                    {aiSuggestion.suggestion}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {aiSuggestion.prompts?.map((prompt, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setFormData({ ...formData, content: prompt });
                          setActiveTab('learn');
                          setShowSuggestion(false);
                        }}
                        className="bg-purple-900/50 hover:bg-purple-800/50 text-purple-300 px-3 py-1.5 font-mono text-xs rounded border border-purple-500/30"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => { setShowSuggestion(false); setAiSuggestion(null); }}
                    className="text-white/80 hover:text-white font-mono text-xs underline"
                  >
                    Fermer
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-purple-900/30 px-4 py-3 border-b border-purple-500/50 flex items-center justify-between">
        <span className="font-mono text-purple-400 text-sm uppercase tracking-wider flex items-center gap-2">
          <Brain className="w-4 h-4" />
          Apprentissage & Reconfiguration
        </span>
        <button
          onClick={fetchEvolutions}
          className="text-purple-400 hover:text-white"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-purple-500/30">
        <button
          onClick={() => setActiveTab('learn')}
          className={`flex-1 px-4 py-2 font-mono text-xs uppercase flex items-center justify-center gap-2 ${
            activeTab === 'learn' 
              ? 'bg-purple-900/30 text-purple-400 border-b-2 border-purple-500' 
              : 'text-white/80 hover:text-white'
          }`}
        >
          <BookOpen className="w-3 h-3" />
          Apprendre
        </button>
        <button
          onClick={() => setActiveTab('config')}
          className={`flex-1 px-4 py-2 font-mono text-xs uppercase flex items-center justify-center gap-2 ${
            activeTab === 'config' 
              ? 'bg-purple-900/30 text-purple-400 border-b-2 border-purple-500' 
              : 'text-white/80 hover:text-white'
          }`}
        >
          <Settings className="w-3 h-3" />
          Configurer
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 px-4 py-2 font-mono text-xs uppercase flex items-center justify-center gap-2 ${
            activeTab === 'history' 
              ? 'bg-purple-900/30 text-purple-400 border-b-2 border-purple-500' 
              : 'text-white/80 hover:text-white'
          }`}
        >
          <History className="w-3 h-3" />
          Historique
        </button>
        <button
          onClick={() => { setActiveTab('gaps'); fetchGaps(); }}
          className={`flex-1 px-4 py-2 font-mono text-xs uppercase flex items-center justify-center gap-2 ${
            activeTab === 'gaps' 
              ? 'bg-purple-900/30 text-amber-400 border-b-2 border-amber-500' 
              : 'text-white/80 hover:text-white'
          }`}
          data-testid="learning-gaps-tab"
        >
          <Lightbulb className="w-3 h-3" />
          Lacunes
          {gaps.length > 0 && (
            <span className="bg-amber-500/30 text-amber-300 text-xs px-1.5 py-0.5 rounded-full ml-1">
              {gaps.length}
            </span>
          )}
        </button>
      </div>

      <div className="p-4">
        {/* Tab: Apprendre */}
        {activeTab === 'learn' && (
          <div className="space-y-4">
            <div className="bg-purple-900/20 p-3 border-l-2 border-purple-500">
              <p className="font-mono text-white/80 text-sm">
                Enseignez de nouvelles informations au système. Elles seront intégrées dans les futures analyses.
              </p>
            </div>

            {/* Type d'apprentissage */}
            <div>
              <label className="font-mono text-white/80 text-xs uppercase block mb-2">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value, target: '' })}
                className="w-full bg-black border border-purple-500/50 text-white p-2 font-mono text-sm"
              >
                <option value="doctrine">Doctrine CESM</option>
                <option value="adversaire">Information Adversaire</option>
                <option value="zone">Information Zone</option>
                <option value="menace">Nouvelle Menace</option>
              </select>
            </div>

            {/* Cible (si adversaire ou zone) */}
            {(formData.type === 'adversaire' || formData.type === 'zone') && (
              <div>
                <label className="font-mono text-white/80 text-xs uppercase block mb-2">
                  {formData.type === 'adversaire' ? 'Adversaire' : 'Zone'}
                </label>
                <select
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                  className="w-full bg-black border border-purple-500/50 text-white p-2 font-mono text-sm"
                >
                  <option value="">Sélectionner...</option>
                  {formData.type === 'adversaire' ? (
                    <>
                      <option value="chine">Chine</option>
                      <option value="russie">Russie</option>
                      <option value="iran">Iran</option>
                      <option value="coree_nord">Corée du Nord</option>
                    </>
                  ) : (
                    <>
                      <option value="pacifique">Pacifique</option>
                      <option value="atlantique">Atlantique</option>
                      <option value="indien">Océan Indien</option>
                      <option value="mediterranee">Méditerranée</option>
                      <option value="hormuz">Détroit d'Ormuz</option>
                      <option value="taiwan">Mer de Chine / Taiwan</option>
                    </>
                  )}
                </select>
              </div>
            )}

            {/* Contenu */}
            <div>
              <label className="font-mono text-white/80 text-xs uppercase block mb-2">Information à apprendre</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Ex: La Chine développe une présence navale croissante dans les îles du Pacifique Sud..."
                className="w-full bg-black border border-purple-500/50 text-white p-3 font-mono text-sm h-32 resize-none"
              />
            </div>

            {/* Bouton */}
            <button
              onClick={handleLearn}
              disabled={loading || !formData.content}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900/50 text-white py-3 font-mono text-sm uppercase flex items-center justify-center gap-2"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Brain className="w-4 h-4" />
                  Enregistrer l'apprentissage
                </>
              )}
            </button>
          </div>
        )}

        {/* Tab: Configurer */}
        {activeTab === 'config' && (
          <div className="space-y-4">
            <div className="bg-blue-900/20 p-3 border-l-2 border-blue-500">
              <p className="font-mono text-white/80 text-sm">
                Modifiez les paramètres du système en temps réel.
              </p>
            </div>

            {/* Quick Config: Priorités */}
            <div className="border border-purple-500/30 p-4">
              <h4 className="font-mono text-purple-400 text-sm uppercase mb-3">Priorités de surveillance</h4>
              <div className="grid grid-cols-2 gap-2">
                {['chine', 'russie', 'iran', 'pacifique'].map(target => (
                  <div key={target} className="flex items-center gap-2">
                    <span className="font-mono text-white/90 text-xs uppercase w-20">{target}</span>
                    <select
                      onChange={(e) => handleConfig('priorite', `priorite ${target}=${e.target.value}`)}
                      className="flex-1 bg-black border border-purple-500/30 text-white p-1 font-mono text-xs"
                      defaultValue=""
                    >
                      <option value="" disabled>Niveau</option>
                      <option value="critique">Critique</option>
                      <option value="eleve">Élevé</option>
                      <option value="modere">Modéré</option>
                      <option value="faible">Faible</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Config: Horaire Digest */}
            <div className="border border-purple-500/30 p-4">
              <h4 className="font-mono text-purple-400 text-sm uppercase mb-3">Horaire rapport quotidien</h4>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  defaultValue="07:00"
                  className="bg-black border border-purple-500/30 text-white p-2 font-mono"
                  onChange={(e) => {
                    const [h, m] = e.target.value.split(':');
                    // Ne pas déclencher automatiquement
                  }}
                  id="digest-time"
                />
                <button
                  onClick={() => {
                    const input = document.getElementById('digest-time');
                    if (input) {
                      const [h, m] = input.value.split(':');
                      handleConfig('digest', `digest ${h}:${m}`);
                    }
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 font-mono text-xs uppercase"
                >
                  Appliquer
                </button>
              </div>
              <p className="font-mono text-white/40 text-xs mt-2">Heure UTC du rapport quotidien automatique</p>
            </div>
          </div>
        )}

        {/* Tab: Historique */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="bg-green-900/20 p-3 border-l-2 border-green-500">
              <p className="font-mono text-white/80 text-sm">
                Historique des modifications et apprentissages du système.
              </p>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {evolutions.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-purple-900/30 sticky top-0">
                    <tr className="font-mono text-white/80 text-xs uppercase">
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2">Action</th>
                      <th className="text-left p-2">Par</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-purple-500/10">
                    {evolutions.map((evo, idx) => (
                      <tr key={idx} className="hover:bg-purple-900/10">
                        <td className="p-2 font-mono text-white/80 text-xs">
                          {new Date(evo.timestamp).toLocaleString('fr-FR', {
                            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                        <td className="p-2">
                          <span className="font-mono text-purple-400 text-xs uppercase">
                            {evo.type}
                          </span>
                        </td>
                        <td className="p-2 font-mono text-white/80 text-xs">
                          {evo.action?.substring(0, 50)}...
                        </td>
                        <td className="p-2 font-mono text-white/80 text-xs">
                          {evo.user_name}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8">
                  <History className="w-8 h-8 text-purple-500/30 mx-auto mb-2" />
                  <p className="font-mono text-white/80 text-sm">Aucune évolution enregistrée</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab: Lacunes */}
        {activeTab === 'gaps' && (
          <div className="space-y-4" data-testid="learning-gaps-content">
            <div className="bg-amber-900/20 p-3 border-l-2 border-amber-500">
              <p className="font-mono text-white/80 text-sm">
                Lacunes détectées par le système lors des analyses. Cliquez pour enseigner l'information manquante.
              </p>
            </div>

            {gapsLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 text-amber-400 animate-spin mx-auto mb-2" />
                <p className="font-mono text-white/60 text-sm">Analyse des lacunes...</p>
              </div>
            ) : gaps.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {gaps.map((gap, idx) => (
                  <div 
                    key={idx} 
                    className="bg-amber-900/10 border border-amber-500/30 p-3 hover:bg-amber-900/20 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="font-mono text-white/90 text-sm">{gap.description || gap}</p>
                        {gap.context && (
                          <p className="font-mono text-white/50 text-xs mt-1">{gap.context}</p>
                        )}
                        {gap.detected_at && (
                          <p className="font-mono text-amber-400/50 text-xs mt-1">
                            Détecté le {new Date(gap.detected_at).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setFormData({ 
                            type: gap.type || 'doctrine', 
                            target: gap.target || '', 
                            content: gap.description || gap 
                          });
                          setActiveTab('learn');
                        }}
                        className="bg-amber-500/20 hover:bg-amber-500/40 text-amber-300 px-3 py-1.5 font-mono text-xs rounded opacity-70 group-hover:opacity-100 transition-opacity whitespace-nowrap"
                        data-testid={`learn-gap-${idx}`}
                      >
                        Enseigner
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Check className="w-8 h-8 text-green-500/50 mx-auto mb-2" />
                <p className="font-mono text-white/80 text-sm">Aucune lacune détectée</p>
                <p className="font-mono text-white/40 text-xs mt-1">Le système semble bien informé</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningPanel;
